/**
 * /es/nuestro-equipo — Rich Spanish team page mirroring /our-team:
 * 4 doctor cards with bio modals, team photo, virtual tour video,
 * facility images, Dr. Mund implants video, commitment statement.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import EsSEO from '@/components/seo/EsSEO';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const DOCTORS = [
  {
    name: 'Dr. Arthur Mund',
    title: 'D.D.S., Fundador',
    bio: 'El Dr. Arthur J. Mund aporta capacitación extensa y más de 40 años de experiencia en odontología cosmética y de implantes en el norte de Texas. Es Diplomado del American Board of Oral Implantology — uno de menos de 500 dentistas en el mundo con esta distinción.',
    image: '/images/team/dr-mund.jpg',
    specialties: ['Implantes Dentales', 'Implantología Oral', 'Odontología Cosmética'],
    fullBio:
      'El Dr. Arthur J. Mund aporta capacitación extensa y más de 40 años de experiencia en odontología cosmética y de implantes al norte de Texas. Se graduó de la Escuela Dental de la Universidad de Texas en Houston en 1982 y se ha convertido en un líder en su campo. Está certificado por el American Board of Oral Implantology y es reconocido por su experiencia en procedimientos de implantes, lo que lo convierte en un dentista solicitado para casos complejos referidos por otros profesionales. Cuando no está en la oficina, el Dr. Mund disfruta pasar tiempo con su familia, conducir sus autos clásicos y devolver a la comunidad a través de iniciativas caritativas.',
  },
  {
    name: 'Dra. Olena Razo',
    title: 'D.D.S.',
    bio: 'La Dra. Olena Razo se graduó de la Escuela Dental de la Universidad de Texas en Houston. Nacida en Ucrania, llegó a Fort Worth a los 8 años y aquí ha hecho su hogar.',
    image: '/images/team/dr-razo.jpg',
    specialties: ['Invisalign', 'Odontología Cosmética', 'Cuidado Preventivo', 'Carillas First Fit'],
    fullBio:
      'La Dra. Olena Razo se graduó de la Escuela Dental de la Universidad de Texas en Houston. Nacida en Ucrania, llegó a Fort Worth a los 8 años y aquí ha hecho su hogar. La Dra. Razo es apasionada de la educación al paciente y el cuidado preventivo. Cree en empoderar a sus pacientes con conocimiento sobre su salud oral, asegurando que tomen decisiones informadas sobre sus tratamientos y bienestar general.',
  },
  {
    name: 'Dr. Omar Hiwiadi',
    title: 'D.M.D.',
    bio: 'El Dr. Omar Hiwiadi nació y creció en Fort Worth. Se especializa en odontología cosmética e implantología avanzada, ofreciendo procedimientos All-on-X, carillas FirstFit® y odontología digital de impresión 3D.',
    image: '/images/team/dr-omar.jpg',
    specialties: ['Odontología Cosmética', 'Implantes Dentales', 'Implantes All-on-X', 'Odontología Restaurativa', 'Carillas First Fit'],
    fullBio:
      'Hola, ¡soy el Dr. Omar Hiwiadi! Nací y crecí aquí en Fort Worth, y mi enfoque hacia la odontología se basa en una filosofía simple: tratar a las personas como familia y escuchar verdaderamente sus necesidades. Creo en trabajar mano a mano con cada persona para crear un plan de tratamiento que se ajuste a sus necesidades únicas y nivel de comodidad.\n\nMe especializo en odontología cosmética e implantología avanzada. Mediante capacitación extensa con los mejores especialistas del país, me enfoco en procedimientos All-on-X "dientes en un día", carillas guiadas FirstFit®, reemplazo de dientes con implantes digitales el mismo día y odontología digital de impresión 3D.\n\nEstudié en la Universidad del Norte de Texas, donde obtuve mi licenciatura en Biología. Luego me capacité en el Midwestern University College of Dental Medicine en Chicago, IL, donde obtuve mi título dental. Practiqué algunos años en Chicago, donde también conocí a mi esposa, ¡quien también es dentista!\n\nCuando no estoy en la oficina, generalmente me encuentras de excursión, explorando ciudades nuevas, o pasando tiempo con familia y amigos. ¡Estoy emocionado de servir a mi comunidad natal de Fort Worth y ayudarte a lograr la sonrisa que mereces!',
  },
  {
    name: 'Dr. Joshua Pham',
    title: 'D.D.S.',
    bio: 'El Dr. Joshua Pham ha residido en el área de DFW por más de 35 años. Obtuvo su Doctorado en Cirugía Dental del Baylor College of Dentistry en Dallas en 2011. Está certificado en LANAP®.',
    image: '/images/team/dr-pham.jpg',
    specialties: ['Tratamiento Láser de Encías LANAP®', 'Odontología General', 'Odontología Familiar', 'Carillas First Fit'],
    fullBio:
      'El Dr. Joshua Pham ha residido en el área de DFW por más de 35 años. Obtuvo su Doctorado en Cirugía Dental del Baylor College of Dentistry en Dallas en 2011. El Dr. Pham asiste regularmente a cursos de educación continua para mantenerse al día con las últimas técnicas y avances en odontología, asegurando que sus pacientes reciban atención moderna y de alta calidad. También está certificado en LANAP (Procedimiento de Nueva Adhesión Asistida por Láser), un tratamiento mínimamente invasivo aprobado por la FDA para enfermedad de las encías moderada a severa. Promueve la regeneración del hueso y tejido, ofreciendo menos dolor, sanación más rápida y mínima recesión de encías.\n\nEl Dr. Pham se enorgullece de ayudar a pacientes de todas las edades a sentirse cómodos, escuchados y seguros en su atención dental.\n\nFuera del consultorio, al Dr. Pham le gusta viajar, también ama cantar, la música y las artes. Como miembro activo de la comunidad, valora conectar con personas y devolver al área que con orgullo ha llamado hogar por décadas.',
  },
];

const SpanishOurTeamPage = () => {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const { displayNumber, telLink } = useSpanishTrackingNumber();

  const teamSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    inLanguage: 'es-US',
    url: `${SITE}/es/nuestro-equipo`,
    name: 'Nuestro Equipo Dental — Fort Worth Dental',
    about: { '@type': 'Dentist', name: 'Fort Worth Dental' },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <EsSEO
        title="Conoce a Nuestro Equipo Dental – Fort Worth Dental"
        description="Conoce a los dentistas de Fort Worth Dental: Dr. Mund (40+ años en implantes), Dra. Razo (Invisalign), Dr. Hiwiadi (All-on-X) y Dr. Pham (LANAP). Atendemos a familias hispanas en Fort Worth."
        canonical="/es/nuestro-equipo"
        hreflangEn="/our-team"
        schema={teamSchema}
      />

      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">
            Conoce al Equipo de Fort Worth Dental
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nuestro equipo de dentistas y especialistas se dedica a ofrecerte la más alta calidad de atención dental en un ambiente cálido y acogedor — siempre en español cuando lo prefieras.
          </p>
        </div>

        {/* Doctor cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-testid="es-team-doctors">
          {DOCTORS.map((doctor) => (
            <div key={doctor.name} className="bg-white rounded-lg shadow hover:shadow-xl transition-shadow text-center p-6">
              <div className="mb-4 flex justify-center">
                <div className="relative w-44 h-52 rounded-full overflow-hidden border-4 border-teal-200">
                  <img
                    src={doctor.image}
                    alt={`${doctor.name} - ${doctor.title} en Fort Worth Dental`}
                    className="w-full h-full object-cover object-top"
                    style={{ objectPosition: 'center 20%' }}
                    loading="lazy"
                  />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">{doctor.name}</h3>
              <p className="text-teal-600 font-medium mb-3 text-sm">{doctor.title}</p>
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">{doctor.bio}</p>
              <button
                onClick={() => setSelectedDoctor(doctor)}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                data-testid={`es-team-readmore-${doctor.name.replace(/\s+/g, '-')}`}
              >
                Leer Sobre {doctor.name.split(' ').slice(0, 2).join(' ')}
              </button>
            </div>
          ))}
        </div>

        {/* Bio modal */}
        {selectedDoctor && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedDoctor(null)}
            data-testid="es-team-bio-modal"
          >
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setSelectedDoctor(null)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full z-10"
                aria-label="Cerrar"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="p-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
                  <div className="w-32 h-40 rounded-lg overflow-hidden border-4 border-teal-200 flex-shrink-0">
                    <img src={selectedDoctor.image} alt={selectedDoctor.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{selectedDoctor.name}</h3>
                    <p className="text-teal-600 font-semibold text-lg">{selectedDoctor.title}</p>
                    {selectedDoctor.specialties && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedDoctor.specialties.map((s) => (
                          <span key={s} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-gray-700 leading-relaxed space-y-4">
                  {selectedDoctor.fullBio.split('\n\n').map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team photo */}
        <div className="mt-16">
          <img
            src="/images/team/team-photo.jpg"
            alt="Foto del equipo de Fort Worth Dental — Dr. Mund, Dra. Razo, Dr. Hiwiadi, Dr. Pham y staff en nuestra oficina dental en Fort Worth TX"
            className="rounded-lg shadow-2xl mx-auto max-w-5xl w-full"
            loading="lazy"
          />
        </div>

        {/* Virtual tour */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Tour Virtual de Nuestra Oficina</h2>
          <div className="aspect-video rounded-lg overflow-hidden shadow-xl">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/phPxGtax2LI"
              title="Tour Virtual — Fort Worth Dental"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>

        {/* Facility images */}
        <div className="mt-16 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Nuestras Instalaciones de Última Generación</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <img
              src="/images/office/building-exterior.png"
              alt="Edificio exterior de Fort Worth Dental en 4620 Citylake Blvd W Fort Worth TX"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
              loading="lazy"
            />
            <img
              src="/images/office/reception-desk.png"
              alt="Área de recepción de Fort Worth Dental con personal amigable"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Dr. Mund implants video */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">Conoce los Implantes Dentales con el Dr. Mund</h2>
          <p className="text-gray-600 text-center mb-8">
            El Dr. Mund, nuestro especialista en implantes, explica el proceso de los implantes dentales y cómo pueden restaurar tu sonrisa.
          </p>
          <div className="aspect-video rounded-lg overflow-hidden shadow-xl">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/qPyKn2Fic0w"
              title="Cómo elegir al dentista correcto para implantes dentales — Dr. Mund"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>

        {/* Commitment */}
        <div className="mt-16 max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Nuestro Compromiso con la Excelencia</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            En Fort Worth Dental, creemos sinceramente que una visita al dentista puede convertirse en un evento esperado. Al cruzar la puerta, nuestro personal amable y profesional te hará sentir como en casa. En cada paso de la cita, nuestro equipo altamente capacitado se ocupará de tus necesidades para que tu visita sea lo más cómoda posible.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed">
            Nos encanta escuchar tus preguntas y darte respuestas completas. ¡Es posible que disfrutes tanto que no quieras irte!
          </p>
        </div>

        {/* CTA */}
        <div className="mt-16 max-w-4xl mx-auto bg-teal-600 text-white p-12 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">¿Listo para Conocer a Nuestro Equipo?</h2>
          <p className="text-xl mb-8">Reserva tu cita hoy — atendemos a familias hispanas todos los días.</p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
            <a href={telLink} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100">
              Llamar a {displayNumber}
            </a>
            <a href="/es/contacto" className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400">
              Reservar Cita
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpanishOurTeamPage;
